import type { KeyboardEvent, ReactNode, RefObject } from "react";
import { createPortal } from "react-dom";
import { transformOrigin } from "./popoverPlacement";
import { usePopoverDismiss } from "./usePopoverDismiss";
import { usePopoverPlacement } from "./usePopoverPlacement";

export interface PopoverProps {
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  align?: "start" | "end";
  /** Open below the anchor (default) or to its right (sidebar flyouts). */
  side?: "bottom" | "right";
  minWidth?: number;
  role?: string;
  ariaLabel?: string;
  className?: string;
  onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void;
}

/** Where the panel waits, hidden, until it has been measured and placed. */
const OFFSCREEN = -9999;

/**
 * Floating panel anchored to a trigger, portaled to <body> so it escapes any
 * overflow container (tables, cards). Flips above when there is no room below.
 */
export function Popover({
  anchorRef,
  open,
  onClose,
  children,
  align = "end",
  side = "bottom",
  minWidth,
  role = "dialog",
  ariaLabel,
  className,
  onKeyDown,
}: PopoverProps): React.JSX.Element | null {
  const { panelRef, position, place } = usePopoverPlacement(anchorRef, open, { align, side });
  usePopoverDismiss(open, { anchorRef, panelRef }, { onClose, place });

  if (!open) return null;

  return createPortal(
    <div
      ref={panelRef}
      role={role}
      aria-label={ariaLabel}
      data-side={position?.side ?? "bottom"}
      className={`nb-popover${className ? ` ${className}` : ""}`}
      style={{
        top: position?.top ?? OFFSCREEN,
        left: position?.left ?? OFFSCREEN,
        minWidth,
        maxHeight: position?.maxHeight,
        overflowY: position?.maxHeight ? "auto" : undefined,
        visibility: position ? "visible" : "hidden",
        ["--origin" as string]: transformOrigin(position?.side, align),
      }}
      onKeyDown={onKeyDown}
    >
      {children}
    </div>,
    document.body,
  );
}
